import clsx from 'clsx';
import React from 'react';
import { Button as ButtonComponent } from 'react-bootstrap';
import ReactHtmlParser from 'react-html-parser';

import useServerSideRendering from '@hooks/useServerSideRendering';
import { generateTrackingAttributes } from '@utils/tracking';

import styles from './styles.module.scss';

export interface ButtonProps {
  className?: string; // can pass extra custom class names to this component
  trackingProps?: { [key: string]: string }; // if you want click-tracking, add properties that match the EventProperties interface in "app/assets/javascripts/tracking.js"
  disabled?: boolean; // if true, button will be disabled
  href?: string; // if passed, the button will be a link
  id?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>; // onClick action for the button
  rel?: string; // used for noreferrer or sponsored links
  rounded?: 'slight' | 'full'; // determines how rounded the button is
  target?: string; // used for links when you want to open in a new tab
  trackClick?: boolean; // if you want to track clicks, set this to true and add trackingProps
  type?: 'button' | 'submit' | 'reset'; // button type, defaults to "button"
  variant?: 'primary' | 'logo' | 'light' | 'info' | 'muted' | 'link' | 'outline'; // determines color of button. If "link" button will look like a link and not a traditional button
  width?: 'full' | 'medium' | 'short'; // if short, button width will be 139px, if full, it will be 100%
  borderWidth?: 'thin' | 'medium'; // determines the thickness of the border
}

export interface ButtonPropsWithChildren extends ButtonProps {
  children: React.ReactNode;
}

export default function Button({
  children,
  className,
  borderWidth,
  disabled,
  href,
  onClick,
  rounded,
  variant,
  trackClick = false,
  trackingProps = {},
  type = 'button',
  width,
  ...restProps
}: ButtonPropsWithChildren) {
  const hydrated = useServerSideRendering();

  if (variant === 'link') {
    return (
      <ButtonComponent
        href={href}
        className={clsx(styles.LinkButton, className || '', {
          'track-click': trackClick,
        })}
        onClick={onClick}
        {...generateTrackingAttributes({
          page: hydrated ? window.location.href : '',
          type: 'link',
          ...trackingProps,
        })}
        {...restProps}
      >
        {children}
      </ButtonComponent>
    );
  }

  return (
    <ButtonComponent
      disabled={disabled}
      href={href}
      type={type}
      className={clsx(styles.Button, className || '', {
        [styles[`${variant}Variant`]]: variant,
        [styles[`${rounded}Rounded`]]: rounded,
        [styles[`${borderWidth}Border`]]: borderWidth,
        [styles[`${width}Width`]]: width,
        'track-click': trackClick,
      })}
      onClick={onClick}
      {...generateTrackingAttributes({
        ...trackingProps,
        page: hydrated ? window.location.href : '',
        type: 'link',
      })}
      {...restProps}
    >
      {typeof children === 'string' ? ReactHtmlParser(children) : children}
    </ButtonComponent>
  );
}
